import { EnvironmentScope, targetParams, useClientTarget } from '@features/clients';
import { ApiError } from '@lib/api';
import { cn } from '@lib/cn';
import { Card, EmptyState, ErrorState, IconArrowLeft, LoadingRows } from '@shared/ui';
import { Link, useParams } from 'react-router';

import { useActivationMuseum } from '../hooks/use-activation-tracking';
import {
    COULDNT_CHECK_NOTE,
    detailDescription,
    detailFacts,
    detailTitle,
    freshnessText,
    toFeatureRow,
    type DatedCell,
    type FeatureRowView,
} from '../model/museum-detail';

import { Td, Th } from './activation-table';
import { FeatureStateBadge, MuseumStatusBadge } from './MuseumStatusBadge';
import { ActivationPageFrame } from './page-frame';

import type { ActivationDetail } from '../schemas/activation';
import type { ReactNode } from 'react';

/**
 * One museum: when it came, who opened the page, and each feature as the page
 * last saw it.
 *
 * Everything here is a snapshot of the museum's last visit, which is why the
 * freshness line sits right beside "Features" and not in a footnote: it is the
 * caveat on every cell of the table under it.
 */
export function ActivationDetailPage() {
    const { tenantId = '' } = useParams();
    const targetState = useClientTarget();
    const { target, isSlotMissing } = targetState;
    const museum = useActivationMuseum(target, tenantId);

    const search = new URLSearchParams(targetParams(target)).toString();

    /** The way out, top-left, carrying the target so the list opens where this was read. */
    const frame = (children: ReactNode) => (
        <ActivationPageFrame>
            <Link
                to={{ pathname: '/activation-tracking', search }}
                className="mb-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-accent-icon transition-colors hover:text-accent-text"
            >
                <IconArrowLeft className="size-3" /> Feature Activation
            </Link>
            <EnvironmentScope state={targetState} />
            {children}
        </ActivationPageFrame>
    );

    if (isSlotMissing) {
        return frame(
            <Card>
                <EmptyState
                    title="No slot chosen"
                    description="Pick a slot above to point at one of the ephemeral deployments."
                />
            </Card>,
        );
    }

    if (museum.isPending) return frame(<LoadingRows label="Loading the museum…" />);

    if (museum.isError) {
        // A 404 is an answer, not a failure: the list only holds museums with
        // Feature activation turned on, and this one is not (or no longer) one.
        if (museum.error instanceof ApiError && museum.error.isNotFound) {
            return frame(
                <Card>
                    <EmptyState
                        title="This museum isn't in Feature activation"
                        description="Either Feature activation is not turned on for its org in this environment, or the link points at another environment. Go back to the list to see the museums that have it."
                        action={
                            <Link
                                to={{ pathname: '/activation-tracking', search }}
                                className="text-[13px] font-semibold text-accent-text hover:underline"
                            >
                                Back to Feature Activation
                            </Link>
                        }
                    />
                </Card>,
            );
        }

        return frame(
            <Card>
                <ErrorState error={museum.error} onRetry={() => void museum.refetch()} />
            </Card>,
        );
    }

    return frame(<MuseumDetail detail={museum.data} />);
}

function MuseumDetail({ detail }: { detail: ActivationDetail }) {
    const facts = detailFacts(detail);
    const rows = detail.features.map((feature) => toFeatureRow(feature));

    return (
        <>
            <div className="mb-6">
                <div className="flex flex-wrap items-center gap-3">
                    <h1 className="text-[28px] font-semibold leading-9 tracking-tight text-content">
                        {detailTitle(detail)}
                    </h1>
                    <MuseumStatusBadge status={detail.status} />
                </div>
                <p className="mt-1 text-[13.5px] text-content-muted">{detailDescription(detail)}</p>
            </div>

            <Card className="mb-5 grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-5 px-[22px] py-5">
                {facts.map((fact) => (
                    <div key={fact.label} className="min-w-0">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-content-muted">
                            {fact.label}
                        </div>
                        <div className="mt-1.5 break-words text-[15px] font-semibold text-content">
                            {fact.value}
                        </div>
                        {fact.sub && (
                            <div className="mt-0.5 text-[12px] text-content-muted">{fact.sub}</div>
                        )}
                    </div>
                ))}
            </Card>

            <Card>
                <div className="flex flex-wrap items-baseline gap-3 border-b border-line-subtle px-[18px] py-4">
                    <h2 className="text-[15px] font-bold text-content">Features</h2>
                    <p className="text-[13px] text-content-muted">{freshnessText(detail)}</p>
                </div>

                {rows.length === 0 ? (
                    <EmptyState
                        title="No features to show"
                        description="The hub sent no feature catalog for this museum."
                    />
                ) : (
                    <div className="overflow-x-auto">
                        {/* Fixed layout, so the columns are the design's and
                            not the content's: Status lands at the same x on
                            every museum, whatever the feature names are. The
                            three date columns share what is left equally. */}
                        <table className="w-full min-w-[820px] table-fixed border-collapse">
                            <colgroup>
                                <col className="w-[22%]" />
                                <col className="w-[20%]" />
                                <col />
                                <col />
                                <col />
                            </colgroup>
                            <thead>
                                <tr>
                                    <Th>Feature</Th>
                                    <Th>Status</Th>
                                    <Th>Checked</Th>
                                    <Th>Started</Th>
                                    <Th>Seen done</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <FeatureRow key={row.id} row={row} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </>
    );
}

function FeatureRow({ row }: { row: FeatureRowView }) {
    const unchecked = row.state === 'unknown';

    return (
        <tr className={cn(unchecked && 'bg-danger-surface/60')}>
            <Td className="align-top text-[14px] font-semibold text-content">{row.title}</Td>
            <Td className="align-top">
                <FeatureStateBadge state={row.state} />
                {unchecked && (
                    <div className="mt-1.5 max-w-[260px] text-[12px] text-danger-muted">
                        {COULDNT_CHECK_NOTE}
                    </div>
                )}
            </Td>
            <DatedTd cell={row.checked} />
            <DatedTd cell={row.started} />
            <DatedTd cell={row.seenDone} />
        </tr>
    );
}

function DatedTd({ cell }: { cell: DatedCell }) {
    return (
        <Td className="whitespace-nowrap align-top text-[14px]">
            <div>{cell.relative}</div>
            {cell.absolute && (
                <div className="mt-0.5 text-[12px] text-content-muted">{cell.absolute}</div>
            )}
        </Td>
    );
}
